/**
 * Rangos de período para charts de actividad/consumo,
 * anclados a la hora local del servidor.
 */
import {
  addServerCalendarDays,
  endOfServerDay,
  getServerAdjustedNow,
  getServerZonedParts,
  serverWallTimeToUtc,
  startOfServerDay,
  startOfServerMonth,
  startOfServerWeekMonday,
  startOfServerYear,
} from './serverTime';

export type ChartPeriodOption =
  | '1hour'
  | '6hours'
  | '12hours'
  | '24hours'
  | '7days'
  | '30days'
  | '12months'
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'thisMonth'
  | 'thisYear'
  | 'custom';

export function getServerChartPeriodRange(
  period: ChartPeriodOption | string,
  customStart: Date | null = null,
  customEnd: Date | null = null,
): { start: Date; end: Date } {
  const now = getServerAdjustedNow();
  switch (period) {
    case '1hour':
      return { start: new Date(now.getTime() - 3600000), end: now };
    case '6hours':
      return { start: new Date(now.getTime() - 6 * 3600000), end: now };
    case '12hours':
      return { start: new Date(now.getTime() - 12 * 3600000), end: now };
    case '24hours':
      return { start: new Date(now.getTime() - 24 * 3600000), end: now };
    case '7days':
      return { start: new Date(now.getTime() - 7 * 86400000), end: now };
    case '30days':
      return { start: new Date(now.getTime() - 30 * 86400000), end: now };
    case '12months':
      return { start: new Date(now.getTime() - 365 * 86400000), end: now };
    case 'yesterday': {
      const y = addServerCalendarDays(now, -1);
      return { start: startOfServerDay(y), end: endOfServerDay(y) };
    }
    case 'thisWeek':
      return { start: startOfServerWeekMonday(now), end: now };
    case 'thisMonth':
      return { start: startOfServerMonth(now), end: now };
    case 'thisYear':
      return { start: startOfServerYear(now), end: now };
    case 'custom':
      return {
        start: customStart || startOfServerDay(now),
        end: customEnd || now,
      };
    case 'today':
    default:
      return { start: startOfServerDay(now), end: now };
  }
}

/** Para alinear ticks de generateTimeRange con reloj de pared del servidor. */
export function alignToServerInterval(date: Date, interval: string): Date {
  const p = getServerZonedParts(date);
  let { year, month, day, hour, minute, second } = p;
  second = 0;
  if (interval === '5minutes') {
    minute = Math.floor(minute / 5) * 5;
  } else if (interval === '15minutes') {
    minute = Math.floor(minute / 15) * 15;
  } else if (interval === '30minutes') {
    minute = Math.floor(minute / 30) * 30;
  } else if (interval === 'minute') {
    // second already 0
  } else if (interval === 'hour') {
    minute = 0;
  } else if (interval === 'day') {
    hour = 0;
    minute = 0;
  }
  return serverWallTimeToUtc(year, month, day, hour, minute, second, 0);
}
